import React, { useCallback, useEffect, useState } from 'react';
import type { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { useDescriptionCompleterAnalytics } from '../common/analytics/index.tsx';
import { DescriptionCompleter } from './description-completer/index.tsx';
import type { Props } from './types.tsx';
import {
	isConfluenceLink,
	generatePayloadForProactiveDescriptionCompleter,
	triggerEditorStreamHubEvent,
} from './utils.tsx';

export const AiProactiveDescriptionCompleter = (props: Props) => {
	const { editorApi, editorExperience, issueSummary } = props;
	const [showFooter, setShowFooter] = useState(false);
	const [currentCard, setCurrentCard] = useState<{ title?: string; url?: string } | undefined>();
	const [triggerType, setTriggerType] = useState<'paste' | 'click' | undefined>();

	const { fireTrack } = useDescriptionCompleterAnalytics();

	const onGenerateClick = useCallback(() => {
		triggerEditorStreamHubEvent(
			generatePayloadForProactiveDescriptionCompleter(currentCard?.url),
			editorExperience,
		);
		fireTrack('proactiveDescriptionCompleter clicked', 'descriptionCompleterFooter', {
			editorExperience,
			triggerType,
		});
	}, [currentCard?.url, editorExperience, fireTrack, triggerType]);

	useEffect(() => {
		// tracking the shared card state plugin
		// determines whether the AI suggest button should be shown or not
		const unsub = editorApi?.card?.sharedState.onChange(({ nextSharedState, prevSharedState }) => {
			if (prevSharedState?.cards.length === nextSharedState?.cards.length) {
				return;
			}
			if (prevSharedState?.cards.length !== 0 || nextSharedState?.cards.length !== 1) {
				setCurrentCard(undefined);
				return;
			}
			const [{ url, title }] = nextSharedState.cards;

			setCurrentCard({ url, title });
			setTriggerType('paste');
			fireTrack('proactiveDescriptionCompleter initiated', 'descriptionCompleterFooter', {
				editorExperience,
				triggerType: 'paste',
			});
		});
		return unsub;
	}, [editorApi, editorExperience, fireTrack]);

	useEffect(() => {
		const unsub = editorApi?.selection?.sharedState.onChange(({ nextSharedState }) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { node } = nextSharedState?.selection as NodeSelection;
			if (node === undefined || node.type.name !== 'inlineCard') {
				return;
			}
			const cardState = editorApi?.card?.sharedState.currentState();
			if (!cardState) {
				return;
			}

			const card = cardState.cards.find((item) => item.url === node.attrs.url);
			if (!card) {
				return;
			}
			const { url, title } = card;

			setCurrentCard({ url, title });
			setTriggerType('click');
			fireTrack('proactiveDescriptionCompleter initiated', 'descriptionCompleterFooter', {
				editorExperience,
				triggerType: 'click',
			});
		});
		return unsub;
	}, [editorApi, editorExperience, fireTrack]);

	useEffect(() => {
		const hasAccess =
			currentCard?.url !== undefined && currentCard?.title !== undefined && issueSummary !== '';
		setShowFooter(hasAccess && isConfluenceLink(currentCard?.url ?? ''));
	}, [currentCard, issueSummary]);

	const onClose = useCallback(() => {
		setShowFooter(false);
		fireTrack('proactiveDescriptionCompleter closed', 'descriptionCompleterFooter', {
			editorExperience,
			triggerType,
		});
	}, [editorExperience, fireTrack, triggerType]);

	return showFooter && <DescriptionCompleter onClick={onGenerateClick} onClose={onClose} />;
};
